import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { ThemeProvider } from "@/components/theme-provider";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { BackToTop } from "@/components/back-to-top";
import { fetchSettings } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from "react";
import { ClientOnly } from "@/lib/utils";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error("Frontend Crash:", error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Terjadi Kesalahan</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Blog tidak dapat dimuat karena gangguan teknis.
        </p>
        <div className="mt-4 p-4 bg-muted/50 rounded-lg text-left overflow-auto max-h-40">
          <code className="text-[10px] text-red-500 whitespace-pre-wrap">
            {error?.message || String(error)}
          </code>
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Coba Lagi
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Halaman Depan
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ context: { queryClient } }) => {
    return await queryClient.ensureQueryData({
      queryKey: ["settings"],
      queryFn: fetchSettings,
    });
  },
  head: ({ loaderData }) => {
    const settings = loaderData as any;
    const siteName = settings?.site_name || "Lihar Aksara";
    const siteDesc =
      settings?.site_description ||
      "Blog editorial untuk sastra, renungan, esai, dan novel. Dibaca pelan-pelan.";
    const siteUrl = "https://liharaksara.web.id";

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: `${siteName} — Ruang Sunyi untuk Membaca` },
        { name: "description", content: siteDesc },
        { name: "author", content: siteName },
        { property: "og:title", content: `${siteName} — Ruang Sunyi untuk Membaca` },
        { property: "og:description", content: siteDesc },
        { property: "og:url", content: siteUrl },
        { property: "og:site_name", content: siteName },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${siteName} — Ruang Sunyi untuk Membaca` },
        { name: "twitter:description", content: siteDesc },
        { name: "theme-color", content: "#F7F6F3" },
        { name: "google-site-verification", content: settings?.google_search_console_id || "" },
        { name: "msvalidate.01", content: "6A02B9AC22F4C7BF24B2064ED933B9D6" },
        { name: "google-adsense-account", content: "ca-pub-9429786539055049" },
      ],
      links: [
        {
          rel: "preconnect",
          href: "https://fonts.googleapis.com",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap",
        },
        {
          rel: "icon",
          type: "image/x-icon",
          href: "/favicon.ico",
        },
        {
          rel: "icon",
          type: "image/svg+xml",
          href: "/favicon.svg",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "192x192",
          href: "/android-chrome-192x192.png",
        },
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        {
          rel: "manifest",
          href: "/site.webmanifest",
        },
        {
          rel: "stylesheet",
          href: appCss,
        },
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: "RSS Feed",
          href: "/api/seo/rss",
        },
        {
          rel: "sitemap",
          type: "application/xml",
          href: "/api/seo/sitemap",
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const loaderData = Route.useLoaderData();
  const settings = loaderData as any;
  return (
    <html lang="id" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                try {
                  var theme = localStorage.getItem('aksara-theme') || 'auto';
                  var resolved = theme;
                  if (theme === 'auto') {
                    resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                  }
                  document.documentElement.classList.toggle('dark', resolved === 'dark');
                } catch (e) {}
              })();
            `,
          }}
        />
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9429786539055049"
          crossOrigin="anonymous"
        />
        {settings?.adsense_pub_id && settings.adsense_pub_id !== "ca-pub-9429786539055049" && (
          <script
            async
            src={
              settings.adsense_auto_ads === "true"
                ? `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${settings.adsense_pub_id}`
                : `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js`
            }
            crossOrigin="anonymous"
          />
        )}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
    </QueryClientProvider>
  );
}

function AppInner() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return (
    <ThemeProvider>
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
        <BackToTop />
        <Toaster />
      </div>
    </ThemeProvider>
  );
}
